import { useEffect, useRef, useState } from 'react'
import { api, type VerificationComment } from '../lib/api.ts'
import { sessionEmail } from '../lib/session.ts'
import { Button } from './Button.tsx'
import { Tooltip } from './Tooltip.tsx'
import { icons } from './icons.ts'
import { FOCUS_RING } from './focusRing.ts'

/**
 * Something to say about the conferência itself.
 *
 * Everything a pharmacist could tell us was attached to a finding: the four
 * reasons ride on "não é divergência", so they need a divergence to ride on. A
 * conferência where everything matched had no way to say anything at all --
 * including the comment that makes the whole comparison worthless, that the
 * receita cannot be read.
 *
 * ## Provisional, like a decision
 *
 * A comment waits with the decisions and is carried into the histórico when
 * Concluir is pressed. It used to go straight into the trail, so it could
 * never be taken back and a sentence somebody thought better of was in the
 * record for good -- which is the opposite of how every other answer on this
 * screen behaves.
 *
 * So the panel lists what is waiting and offers to remove it, and the label is
 * "Adicionar" rather than "Enviar": nothing is sent anywhere, it is added to a
 * list that this conferência will commit when it concludes.
 *
 * On one already concluded there is no conclusion coming to carry it, so that
 * comment goes to the trail directly -- and cannot be taken back, which the
 * panel says rather than pretending otherwise.
 */
export function FeedbackButton({
  verificationId,
  comments,
  onChanged,
  settled = false,
}: {
  verificationId: string
  /** What is waiting, from the detail. */
  comments: readonly VerificationComment[]
  /** Ask the screen to reload the detail, so the count follows. */
  onChanged: () => void
  /** A concluded conferência commits immediately and cannot take one back. */
  settled?: boolean
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  /*
   * What this panel has changed that the detail has not caught up with yet.
   *
   * A comment used to appear only once the screen had reloaded the whole
   * conferência -- a beat after pressing Adicionar, long enough to look like
   * nothing happened. It is drawn on the press instead, and dropped from here
   * once the detail carries it (or gone, for a removal). On failure the
   * change is undone and the text goes back in the box.
   */
  const [added, setAdded] = useState<readonly VerificationComment[]>([])
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set())
  const panel = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLTextAreaElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const known = new Set(comments.map((c) => c.id))
  const shown = [...comments, ...added.filter((c) => !known.has(c.id))].filter(
    (c) => !removed.has(c.id),
  )

  /*
   * Let go of what the detail now says for itself.
   *
   * Held any longer, an added line would outlive its own conclusion: Concluir
   * carries the comments into the histórico and out of this list, and a copy
   * kept here would bring it straight back.
   */
  useEffect(() => {
    setAdded((current) => {
      const next = current.filter((c) => !known.has(c.id))
      return next.length === current.length ? current : next
    })
    setRemoved((current) => {
      const next = new Set([...current].filter((id) => known.has(id)))
      return next.size === current.size ? current : next
    })
    // `known` is derived from `comments`, which is what this follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments])

  // The newest at the bottom is the one just written, so the list follows it.
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight })
  }, [shown.length])

  const close = () => {
    panel.current?.hidePopover()
    setError(undefined)
  }

  const add = async () => {
    const trimmed = text.trim()
    // The button exists to capture words somebody meant to write; a blank one
    // records only that they opened a box.
    if (trimmed === '') {
      setError('Escreva o que aconteceu.')
      return
    }
    setError(undefined)
    /*
     * On a concluded conferência the comment goes straight to the histórico
     * and never joins this list, so there is nothing to draw ahead of the
     * server -- only an open one gets the instant line.
     */
    const draft: VerificationComment = {
      id: `pending-${Date.now()}`,
      author_email: sessionEmail() ?? '',
      body: trimmed,
      created_at: new Date().toISOString(),
    }
    if (!settled) {
      setAdded((current) => [...current, draft])
      setText('')
    }
    setBusy(settled)
    try {
      const result = await api.addComment(verificationId, trimmed)
      if (settled) setText('')
      // Swap the draft for the stored row, so a removal targets a real id.
      setAdded((current) =>
        current.map((c) => (c.id === draft.id && result.comment ? result.comment : c)),
      )
      onChanged()
      field.current?.focus()
    } catch (cause) {
      /*
       * Undone, with the text back in the box. Losing what they wrote on a
       * failure means somebody who has just been told it failed will not
       * type it a second time.
       */
      setAdded((current) => current.filter((c) => c.id !== draft.id))
      setText(trimmed)
      setError(cause instanceof Error ? cause.message : 'não foi possível adicionar')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (commentId: string) => {
    setError(undefined)
    setRemoved((current) => new Set([...current, commentId]))
    try {
      await api.removeComment(verificationId, commentId)
      onChanged()
    } catch (cause) {
      setRemoved((current) => new Set([...current].filter((id) => id !== commentId)))
      setError(cause instanceof Error ? cause.message : 'não foi possível remover')
    }
  }

  const count = shown.length

  return (
    <>
      <Tooltip
        text={
          count === 0
            ? 'Comentar a conferência — algo que não cabe em um apontamento, como uma receita ilegível.'
            : `${count} comentário${count === 1 ? '' : 's'} nesta conferência. Serão gravados ao concluir.`
        }
      >
        <span className="relative inline-flex">
          <Button
            variant="secondary"
            tone="neutral"
            size="icon"
            leadingIcon={icons.feedback}
            aria-label={
              count === 0
                ? 'Comentar a conferência'
                : `Comentar a conferência, ${count} já adicionado${count === 1 ? '' : 's'}`
            }
            onClick={() => {
              setError(undefined)
              // Positioned before it is shown: a popover shown from a node
              // that has not been measured paints at the window corner.
              queueMicrotask(() => {
                panel.current?.showPopover()
                field.current?.focus()
              })
            }}
          />
          {/* The count on the icon, because whether anything is waiting is the
              question somebody has before they open it. */}
          {count > 0 && (
            <span
              aria-hidden="true"
              className="bg-fill-brand-strong text-tTiny pointer-events-none absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full text-white"
            >
              {count}
            </span>
          )}
        </span>
      </Tooltip>

      {/*
        Always mounted and toggled, never conditionally rendered: a panel that
        unmounts on close comes back as a new element, and showPopover() on a
        node that has not been positioned paints it at the window corner.
      */}
      <div
        ref={panel}
        popover="auto"
        role="dialog"
        aria-label="Comentários da conferência"
        /*
         * As small as what is in it, and no taller than a ceiling.
         *
         * The panel used to grow with every comment until it outgrew the
         * window. A fixed height fixed that and made an empty panel mostly
         * empty space. Now it sizes to its content, and the list alone stops
         * growing at a few comments and scrolls -- so the field being typed in
         * stays where it is once the list is full.
         * `open:flex`, not `flex`: a popover is display:none until shown, and
         * a display class on the closed element would override that.
         */
        className="rounded-base border-stroke-weak bg-background-raised m-auto max-h-[85vh] w-[34rem] max-w-[90vw] flex-col border p-4 shadow-lg open:flex"
      >
        <span className="text-tH6 text-text-strong mb-1 block shrink-0">
          Comentários da conferência
        </span>
        <span className="text-tSmall text-text-weak mb-3 block shrink-0">
          {settled
            ? 'Esta conferência já foi concluída, então o comentário é gravado na hora e não pode ser removido.'
            : 'Para o que não cabe em um apontamento — uma receita ilegível, algo que faltou. Não altera o resultado, e só é gravado ao concluir.'}
        </span>

        {shown.length > 0 && (
          <ul
            ref={list}
            className="mb-3 flex max-h-[16rem] min-h-0 list-none flex-col gap-2 overflow-y-auto p-0"
          >
            {shown.map((comment) => (
              <li
                key={comment.id}
                className="rounded-base border-stroke-weak bg-background-sunken flex shrink-0 items-center gap-2 border p-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="text-tTiny text-text-weak mb-1 block">
                    {comment.author_email}
                  </span>
                  <span className="text-tP text-text-strong break-words whitespace-pre-wrap">
                    {comment.body}
                  </span>
                </span>
                {/* Only while it is still a draft. Once the conclusion has
                    carried it into the histórico it is a record somebody
                    signed, not a line to delete. The same trash button the
                    product page's synonyms use, centred on the row. */}
                <Button
                  size="small"
                  variant="secondary"
                  tone="danger"
                  aria-label="Remover comentário"
                  title="Remover"
                  leadingIcon={icons.remove}
                  // A line still on its way has no id the server knows yet.
                  disabled={busy || comment.id.startsWith('pending-')}
                  onClick={() => void remove(comment.id)}
                />
              </li>
            ))}
          </ul>
        )}

        <textarea
          ref={field}
          value={text}
          disabled={busy}
          rows={3}
          maxLength={2000}
          placeholder="Ex.: a receita está ilegível na parte da posologia."
          /* The field the rest of the screen draws: Input has no multiline
             form, so its border, fill and radius are matched by hand. */
          className={`bg-fill-white border-stroke-strong text-tP text-text-strong hover:bg-fill-hover focus-within:border-stroke-selected w-full shrink-0 resize-none rounded-[var(--radius-base)] border px-3 py-2 transition-[background-color,border-color] duration-150 ${FOCUS_RING}`}
          onChange={(event) => setText(event.target.value)}
          // Enter on its own is a new line; Ctrl/⌘+Enter adds, as chat boxes do.
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault()
              void add()
            }
          }}
        />
        {error !== undefined && (
          <span className="text-tSmall text-text-error mt-1 block shrink-0">{error}</span>
        )}

        <div className="mt-3 flex shrink-0 gap-2">
          {/* `size` defaults to `large` -- h-14, text-xl -- which is a page's
              primary action rather than a dialog's. Matched to the reason
              panel, the precedent for a dialog on this screen. */}
          <Button
            size="small"
            variant="primary"
            tone="brand"
            onClick={() => void add()}
            disabled={busy}
          >
            {busy ? 'Adicionando…' : 'Adicionar'}
          </Button>
          <Button size="small" variant="tertiary" tone="neutral" onClick={close} disabled={busy}>
            Fechar
          </Button>
        </div>
      </div>
    </>
  )
}
